import { Form, Formik, useField } from 'formik';
import { Eye, EyeOff, KeyRound, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuthService } from '@/services/auth.service';
import { getInitialValues, getValidationSchema } from '@/utils/validation';

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type FieldType = {
  oldPassword: string;
  newPassword: string;
  newPasswordConfirmation: string;
};

function PasswordField({
  name,
  label,
  autoComplete,
}: {
  name: keyof FieldType;
  label: string;
  autoComplete: string;
}) {
  const [field, meta] = useField<string>(name);
  const [visible, setVisible] = useState(false);
  const hasError = Boolean(meta.touched && meta.error);

  return (
    <label className="block">
      <span className="mb-[0.7rem] block text-[1.3rem] font-medium leading-[1.8rem] text-main">
        {label}
      </span>
      <span className="relative block">
        <input
          {...field}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          aria-invalid={hasError}
          className={`h-[4.6rem] w-full rounded-[1rem] border bg-main px-[1.4rem] pr-[4.8rem] text-[1.4rem] text-main outline-none transition-[border-color,box-shadow] placeholder:text-disable focus:border-[#57E8BA] focus:ring-[0.3rem] focus:ring-[#57E8BA]/15 ${
            hasError ? 'border-red-500' : 'border-primary'
          }`}
          placeholder={`Enter ${label.toLowerCase()}`}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          style={{ position: 'absolute', right: '0.7rem', top: '50%', transform: 'translateY(-50%)' }}
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          className="h-[3.2rem] w-[3.2rem] rounded-[0.7rem] border-0 bg-transparent p-0 text-second shadow-none hover:bg-secondary hover:text-main dark:bg-transparent"
        >
          {visible ? <EyeOff className="h-[1.8rem] w-[1.8rem]" /> : <Eye className="h-[1.8rem] w-[1.8rem]" />}
        </Button>
      </span>
      {hasError && <span className="mt-[0.6rem] block text-[1.2rem] leading-[1.6rem] text-red-500">{meta.error}</span>}
    </label>
  );
}

export default function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const { changePassword } = useAuthService();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        style={{ width: 'calc(100% - 3.2rem)', maxWidth: '46rem' }}
        className="gap-0 overflow-hidden !rounded-[1.6rem] border border-primary bg-secondary p-0 text-main shadow-[0_2rem_6rem_rgba(0,0,0,0.22)]"
      >
        <div className="relative border-b border-primary px-[2rem] pb-[1.8rem] pt-[2rem] sm:px-[2.4rem] sm:pb-[2rem] sm:pt-[2.4rem]">
          <div className="flex items-start gap-[1.2rem] pr-[3.6rem]">
            <span className="grid h-[4rem] w-[4rem] shrink-0 place-items-center rounded-[1rem] bg-[#57E8BA]/15 text-[#087C59] dark:text-[#57E8BA]">
              <KeyRound className="h-[1.9rem] w-[1.9rem]" />
            </span>
            <div>
              <DialogTitle className="text-[2rem] font-semibold leading-[2.6rem] text-main">Change password</DialogTitle>
              <DialogDescription className="mt-[0.4rem] text-[1.3rem] leading-[1.8rem] text-second">
                Use a strong password you don’t use elsewhere.
              </DialogDescription>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            aria-label="Close change password dialog"
            className="absolute right-[1.2rem] top-[1.2rem] h-[3.2rem] w-[3.2rem] rounded-[0.8rem] text-second hover:text-main"
          >
            <X className="h-[1.8rem] w-[1.8rem]" />
          </Button>
        </div>

        <Formik<FieldType>
          initialValues={getInitialValues('changePassword') as FieldType}
          validationSchema={getValidationSchema('changePassword')}
          onSubmit={async (values, { setStatus, setSubmitting, resetForm }) => {
            setStatus(undefined);
            const response = await changePassword(values.oldPassword, values.newPassword);
            setSubmitting(false);

            if (response?.isSuccess) {
              resetForm();
              onOpenChange(false);
              return;
            }

            setStatus(response?.errors?.[0]?.message ?? 'Unable to change password. Please try again.');
          }}
        >
          {({ isSubmitting, isValid, dirty, status }) => (
            <Form>
              <div className="space-y-[1.6rem] px-[2rem] py-[2rem] sm:px-[2.4rem] sm:py-[2.4rem]">
                <PasswordField name="oldPassword" label="Current password" autoComplete="current-password" />
                <PasswordField name="newPassword" label="New password" autoComplete="new-password" />
                <PasswordField name="newPasswordConfirmation" label="Confirm new password" autoComplete="new-password" />
                <p className="text-[1.2rem] leading-[1.7rem] text-second">Your new password must contain at least 8 characters.</p>
                {status && (
                  <p role="alert" className="rounded-[0.8rem] bg-red-500/10 px-[1.2rem] py-[1rem] text-[1.25rem] leading-[1.7rem] text-red-500">
                    {status}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-[1rem] border-t border-primary px-[2rem] py-[1.6rem] sm:px-[2.4rem]">
                <Button type="button" variant="outline" size="sz44" disabled={isSubmitting} onClick={() => onOpenChange(false)} className="w-full text-[1.4rem]">
                  Cancel
                </Button>
                <Button type="submit" variant="default" size="sz44" loading={isSubmitting} disabled={!dirty || !isValid || isSubmitting} className="w-full text-[1.4rem]">
                  Change password
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
